import React, { useState, useEffect, useRef } from "react";
import { Upload, Play, RefreshCw, AlertTriangle, CheckCircle2, Database, Info, FileSpreadsheet, GitCompareArrows, Download, Zap, Sparkles, ChevronRight, GitMerge } from "lucide-react";
import { PageHead, Loader } from "../components/PageHead";
import api, { apiErr, API } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
import { toast } from "sonner";

// Raw fetch() (needed for multipart FormData uploads) bypasses the axios
// `api` instance's X-Env interceptor (lib/api.js) — without this, uploads
// always land in db_live while the polling GET (which uses `api`) reads
// whichever env the toggle is on, causing an endless "Importing..." stall
// whenever the UI is in the default Test env. Mirror that interceptor here.
function authHeaders() {
  const token = localStorage.getItem("pte_token");
  const env = localStorage.getItem("pte_env") || "test";
  return { Authorization: `Bearer ${token}`, "X-Env": env };
}

export default function DataImport({ embedded = false }) {
  const [batches, setBatches] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [errBatch, setErrBatch] = useState(null);
  const [errors, setErrors] = useState(null);

  const loadBatches = () => api.get("/import/batches").then((r) => setBatches(r.data)).catch(() => {});
  useEffect(() => { loadBatches(); const t = setInterval(loadBatches, 5000); return () => clearInterval(t); }, []);

  const run = async (dry_run, monolith = false) => {
    try { await api.post("/import/run-migration", null, { params: { dry_run, monolith } }); toast.success(`Migration started (${dry_run ? "dry-run" : "commit"}${monolith ? " · monolith" : ""})`); setTimeout(loadBatches, 800); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const analyze = async (file) => {
    if (!file) return;
    setAnalyzing(true); setAnalysis(null);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } }); setAnalysis(r.data); }
    catch (e) { toast.error(apiErr(e)); } finally { setAnalyzing(false); }
  };

  const viewErrors = async (b) => {
    setErrBatch(b); setErrors(null);
    try { const r = await api.get("/import/errors", { params: { batch_id: b.id, limit: 200 } }); setErrors(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      {!embedded && (
        <PageHead crumb="Admin" title="Data Import"
          sub="Caspio → clean schema. Idempotent upsert on documented keys, dry-runnable, with a validation report." />
      )}

      <div className="notice" style={{ marginBottom: 18 }}>
        <Info size={16} />
        <span>Discards per spec: <b>Sub_Customer_Table</b> (26M junk), <b>Statement</b> (Sage), legacy <span className="mono">Cal_Due_*/F*</span> columns (recomputed). Plaintext passwords are never imported — customers reset on first login.</span>
      </div>

      <div className="grid cols-2" style={{ marginBottom: 18 }}>
        <CaspioEquipmentCsvCard onChange={loadBatches} />

        <div className="card pad">
          <div className="sec-h"><h3>Analyze a file</h3><Upload size={18} color="var(--gold-deep)" /></div>
          <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>Upload an Excel/CSV export to auto-detect tables, fields, sample values & row counts with the pre-loaded mapping.</p>
          <label className="btn ghost" style={{ cursor: "pointer" }}>
            <Upload size={14} /> {analyzing ? "Analyzing…" : "Choose .xlsx / .csv"}
            <input type="file" accept=".xlsx,.csv" hidden onChange={(e) => analyze(e.target.files[0])} data-testid="analyze-file" />
          </label>
        </div>

        <CaspioDiffCard />

        <SageCustomerImportCard onChange={loadBatches} />

        <SageVendorImportCard onChange={loadBatches} />

        <TakeOnCleanupWizardCard />

        <div className="card pad" style={{ gridColumn: "1 / -1" }}>
          <div className="sec-h"><h3>Run bundled migration (legacy)</h3><Database size={18} color="var(--gold-deep)" /></div>
          <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>Process the original bundled Caspio export (3 workbooks). Use the Equipment CSV importer above for the live take-on flow.</p>
          <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
            <button className="btn ghost" onClick={() => run(true)} data-testid="run-dryrun"><Play size={14} /> Dry-run (core)</button>
            <button className="btn gold" onClick={() => run(false)} data-testid="run-commit"><CheckCircle2 size={14} /> Commit (core)</button>
            <button className="btn dark" onClick={() => run(false, true)} data-testid="run-monolith"><Database size={14} /> Commit full history</button>
          </div>
        </div>
      </div>

      {analysis && (
        <div className="card pad" style={{ marginBottom: 18 }}>
          <div className="sec-h"><h3>{analysis.filename} — {analysis.table_count} table(s)</h3></div>
          <div className="tbl-wrap">
            <table data-testid="analysis-table">
              <thead><tr><th>Table</th><th>Rows</th><th>Fields</th><th>Suggested destination</th></tr></thead>
              <tbody>{analysis.tables.map((t) => (
                <tr key={t.name}><td className="t-strong">{t.name}</td><td className="mono t-mut">{t.rows.toLocaleString()}</td><td className="mono t-mut">{t.fields.length}</td>
                  <td style={{ color: t.suggested_destination.includes("DO NOT") ? "var(--red)" : "var(--text-2)", fontSize: 12 }}>{t.suggested_destination}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sec-h"><h3>Import batches</h3><span className="link" onClick={loadBatches}><RefreshCw size={13} style={{ verticalAlign: "-2px" }} /> Refresh</span></div>
      <div className="tbl-wrap" style={{ marginBottom: 18 }}>
        <table data-testid="batches-table">
          <thead><tr><th>Source</th><th>Mode</th><th>Status</th><th>Read</th><th>Imported</th><th>Errored</th><th>Started</th><th></th></tr></thead>
          <tbody>
            {batches.length === 0 && <tr><td colSpan={8} className="t-mut">No import batches yet.</td></tr>}
            {batches.map((b) => (
              <tr key={b.id}>
                <td className="t-strong" style={{ fontSize: 12.5 }}>{b.source_file}</td>
                <td>{b.dry_run ? <span className="badge b-slate"><span className="bd" />Dry-run</span> : <span className="badge b-gold"><span className="bd" />Commit</span>}</td>
                <td><span className={`badge ${b.status === "complete" ? "b-green" : b.status === "failed" ? "b-red" : "b-blue"}`}><span className="bd" />{b.status}</span></td>
                <td className="mono t-mut">{(b.rows_read || 0).toLocaleString()}</td>
                <td className="mono t-strong">{(b.rows_imported || 0).toLocaleString()}</td>
                <td className="mono" style={{ color: b.rows_errored ? "var(--amber)" : "var(--text-2)" }}>{(b.rows_errored || 0).toLocaleString()}</td>
                <td className="mono t-mut">{fmtDate(b.started_at)}</td>
                <td><button className="btn sm ghost" onClick={() => viewErrors(b)} data-testid={`batch-errors-${b.id}`}><AlertTriangle size={13} /> Report</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {errBatch && (
        <div className="card pad">
          <div className="sec-h"><h3>Validation report</h3><span className="link" onClick={() => setErrBatch(null)}>Close</span></div>
          {!errors ? <Loader /> : (
            <>
              <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>{errors.total.toLocaleString()} validation entries · reconciliation: imported + errored = rows read (minus documented discards).</p>
              <div className="tbl-wrap">
                <table><thead><tr><th>Severity</th><th>Source</th><th>Rule</th><th>Detail</th></tr></thead>
                  <tbody>{errors.items.slice(0, 200).map((e) => (
                    <tr key={e.id}><td><span className={`badge ${e.severity === "error" ? "b-red" : "b-amber"}`}><span className="bd" />{e.severity}</span></td>
                      <td className="t-mut">{e.source_table}</td><td className="mono t-mut" style={{ fontSize: 12 }}>{e.rule}</td><td className="t-mut" style={{ fontSize: 12 }}>{e.detail}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}


function CaspioEquipmentCsvCard({ onChange }) {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const start = async () => {
    if (!file) return toast.error("Pick a Caspio Equipment_Database CSV first");
    setBusy(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");

      const resp = await fetch(`${API}/import/equipment-csv`, {
        method: "POST", body: fd, headers: authHeaders(),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || resp.statusText);
      }
      const { batch_id } = await resp.json();
      toast.success(`Import started in ${dryRun ? "dry-run" : "commit"} mode`);
      // Poll status every 2s
      const tick = async () => {
        try {
          const r = await api.get(`/import/batches/${batch_id}`);
          setBatch(r.data);
          if (r.data.status === "completed" || r.data.status === "failed") {
            clearInterval(pollRef.current); pollRef.current = null;
            setBusy(false);
            onChange && onChange();
          }
        } catch (_) {}
      };
      pollRef.current = setInterval(tick, 2000);
      tick();
    } catch (e) {
      toast.error(e.message || apiErr(e));
      setBusy(false);
    }
  };

  const c = batch?.counts || {};
  const pct = busy && c.rows_seen ? Math.min(99, Math.floor((c.rows_seen / Math.max(c.rows_seen + 200, 1)) * 100)) : null;

  return (
    <div className="card pad" style={{ borderLeft: "3px solid var(--gold-champ)" }}>
      <div className="sec-h"><h3>Caspio Equipment CSV (take-on)</h3><FileSpreadsheet size={18} color="var(--gold-deep)" /></div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>
        Upload the wide Caspio <code className="mono">Equipment_Database</code> export. The importer derives <b>customers</b>, <b>sub-contractors</b>, <b>equipment</b> and <b>certificates</b> from this single file — no other uploads needed. Re-running the same (or larger) export creates <b>zero duplicates</b> thanks to a stable per-row dedup key.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label className="btn ghost" style={{ cursor: "pointer" }}>
          <Upload size={14} /> {file ? "Replace file" : "Choose CSV"}
          <input type="file" accept=".csv" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="caspio-csv-file" disabled={busy} />
        </label>
        {file && <span className="t-mut" style={{ fontSize: 12 }}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
      </div>

      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 12 }}>
        <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={busy} data-testid="caspio-dryrun" />
        Dry-run (count rows, don't write to DB)
      </label>

      <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
        <button className="btn gold" onClick={start} disabled={busy || !file} data-testid="caspio-import-start">
          <Play size={14} /> {busy ? "Importing…" : (dryRun ? "Run dry-run" : "Import now")}
        </button>
      </div>

      {batch && (
        <div className="card pad" style={{ marginTop: 14, background: "var(--card-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className={`badge ${batch.status === "completed" ? "b-green" : batch.status === "failed" ? "b-red" : "b-blue"}`}>
              <span className="bd" />{batch.status}
            </span>
            <span className="s t-mut mono">{batch.dry_run ? "dry-run" : "commit"} · {batch.filename}</span>
            {pct != null && <span className="s t-mut">~{pct}%</span>}
          </div>
          {batch.error && <div style={{ color: "var(--red)", fontSize: 12.5, marginBottom: 8 }}>{batch.error}</div>}
          <div className="grid" style={{ gridTemplateColumns: "repeat(2, 1fr)", gap: 6, fontSize: 12.5 }}>
            <KV label="Rows seen" value={c.rows_seen} />
            <KV label="Rows skipped (blank customer)" value={c.rows_skipped_blank} />
            <KV label="Customers created" value={c.customers_created} highlight />
            <KV label="Customers matched" value={c.customers_matched} />
            <KV label="Sub-contractors created" value={c.subcons_created} highlight />
            <KV label="Equipment created" value={c.equipment_created} highlight />
            <KV label="Equipment enriched (existing matches updated)" value={c.equipment_enriched} />
            <KV label="Active-lab → closed override (invoiced)" value={c.equipment_active_override_closed} />
            <KV label="Certificates created" value={c.certificates_created} highlight />
            <KV label="Errors" value={c.errors} danger={c.errors > 0} />
          </div>
          {batch.finished_at && <div className="s t-mut mono" style={{ marginTop: 8, fontSize: 11 }}>Finished {fmtDateTime(batch.finished_at)}</div>}
          {c.sample_errors && c.sample_errors.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--amber)" }}>{c.sample_errors.length} sample error(s)</summary>
              <ul style={{ fontSize: 11.5, marginTop: 6 }}>
                {c.sample_errors.map((e, i) => <li key={`${e.row}-${i}`}>Row {e.row}: {e.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, value, highlight, danger }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
      <span className="t-mut">{label}</span>
      <span className="mono" style={{ fontWeight: highlight ? 700 : 500, color: danger ? "var(--red)" : (highlight ? "var(--gold-deep)" : "var(--text-1)") }}>
        {(value ?? 0).toLocaleString()}
      </span>
    </div>
  );
}


// ============================================================
//  Sage (Pastel) customer one-off importer — "PTE Connect Upload" sheet
// ============================================================
function SageCustomerImportCard({ onChange }) {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lastUndo, setLastUndo] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const loadLastUndo = () => api.get("/import/sage-customers/last-undo")
    .then((r) => setLastUndo(r.data?.available ? r.data : null)).catch(() => {});
  useEffect(() => { loadLastUndo(); }, []);

  const start = async () => {
    if (!file) return toast.error("Choose the Sage customer .xlsx first");
    if (!dryRun && !window.confirm(
      "Commit the Sage customer import?\n\n" +
      "Matched customers are updated (blank cells never overwrite existing data) " +
      "and unmatched rows are created. Extra ';'-separated contacts are added. " +
      "Run a dry-run first if unsure. This is audited and can be undone."
    )) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");

      const resp = await fetch(`${API}/import/sage-customers`, {
        method: "POST", body: fd, headers: authHeaders(),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || resp.statusText);
      }
      const data = await resp.json();
      setResult(data);
      toast.success(dryRun ? "Dry-run complete — review the preview below" : "Sage customers imported");
      loadLastUndo();
      onChange && onChange();
    } catch (e) {
      toast.error(e.message || apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!lastUndo) return;
    if (!window.confirm(
      `Undo the last Sage import (${lastUndo.filename})?\n\n` +
      `This will DELETE ${lastUndo.created_customers} customer(s) it created, ` +
      `delete ${lastUndo.created_contacts} contact(s) it added, and restore ` +
      `${lastUndo.updated_customers} updated customer(s) to their previous values. ` +
      `This cannot be redone.`
    )) return;
    setUndoing(true);
    try {
      const fd = new FormData();
      fd.append("undo_id", lastUndo.id);

      const resp = await fetch(`${API}/import/sage-customers/undo`, {
        method: "POST", body: fd, headers: authHeaders(),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || resp.statusText);
      }
      const d = await resp.json();
      toast.success(`Undone — ${d.customers_deleted} deleted, ${d.customers_restored} restored, ${d.contacts_deleted} contact(s) removed`);
      setLastUndo(null);
      onChange && onChange();
    } catch (e) {
      toast.error(e.message || apiErr(e));
    } finally {
      setUndoing(false);
    }
  };

  const r = result || {};
  const updated = (r.updated_by_id || 0) + (r.updated_by_number || 0) + (r.updated_by_name || 0);

  return (
    <div className="card pad" style={{ borderLeft: "3px solid var(--gold-deep)", gridColumn: "1 / -1" }} data-testid="sage-import-card">
      <div className="sec-h"><h3>Sage customer load &amp; correct (one-off)</h3><GitMerge size={18} color="var(--gold-deep)" /></div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>
        Upload the merged Sage workbook (<code className="mono">PTE_Connect_Customers_Sage…xlsx</code>). Reads the <b>PTE Connect Upload</b> sheet.
        Existing customers are matched by <b>id → customer number → name</b> and <b>updated</b> (blank cells never wipe existing data); rows with no match are <b>created new</b>.
        Multiple <span className="mono">;</span>-separated values in <b>email / contact / phone</b> are split — the first is the primary, the rest are added as <b>contacts</b> for that client.
      </p>

      {lastUndo && (
        <div className="card pad" style={{ marginBottom: 12, background: "rgba(200,140,40,.08)", borderLeft: "3px solid var(--amber)" }} data-testid="sage-undo-banner">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <AlertTriangle size={15} color="var(--amber)" />
            <span style={{ fontSize: 12.5 }}>
              Last commit (<b>{lastUndo.filename}</b>) can be undone — created <b>{lastUndo.created_customers}</b>, updated <b>{lastUndo.updated_customers}</b>, +{lastUndo.created_contacts} contacts.
            </span>
            <button className="btn sm" onClick={undo} disabled={undoing} data-testid="sage-undo-btn" style={{ marginLeft: "auto" }}>
              <RefreshCw size={13} className={undoing ? "spin" : ""} /> {undoing ? "Undoing…" : "Undo last import"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label className="btn ghost" style={{ cursor: "pointer" }}>
          <Upload size={14} /> {file ? "Replace file" : "Choose .xlsx"}
          <input type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="sage-file" disabled={busy} />
        </label>
        {file && <span className="t-mut" style={{ fontSize: 12 }}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={busy} data-testid="sage-dryrun" />
          Dry-run (preview only, no writes)
        </label>
        <button className="btn gold" onClick={start} disabled={busy || !file} data-testid="sage-import-start" style={{ marginLeft: "auto" }}>
          <Play size={14} /> {busy ? "Working…" : (dryRun ? "Run dry-run" : "Import now")}
        </button>
      </div>

      {result && (
        <div className="card pad" style={{ marginTop: 8, background: "var(--card-2)" }} data-testid="sage-result">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className={`badge ${r.dry_run ? "b-slate" : "b-green"}`}><span className="bd" />{r.dry_run ? "Dry-run preview" : "Committed"}</span>
            <span className="s t-mut mono">{(r.rows_read || 0).toLocaleString()} rows read</span>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 6, fontSize: 12.5 }}>
            <KV label="Customers created" value={r.customers_created} highlight />
            <KV label="Customers updated" value={updated} highlight />
            <KV label="Fields updated" value={r.fields_updated} />
            <KV label="Matched by id" value={r.updated_by_id} />
            <KV label="Matched by number" value={r.updated_by_number} />
            <KV label="Matched by name" value={r.updated_by_name} />
            <KV label="Primary contacts created" value={r.primary_contacts_created} />
            <KV label="Extra contacts created" value={r.extra_contacts_created} highlight />
            <KV label="Extra contacts existed" value={r.extra_contacts_existed} />
            <KV label="Skipped (blank name)" value={r.skipped_blank_name} />
            <KV label="Errors" value={r.total_errors} danger={r.total_errors > 0} />
          </div>
          {r.errors && r.errors.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--amber)" }}>{r.errors.length} sample error(s)</summary>
              <ul style={{ fontSize: 11.5, marginTop: 6 }}>
                {r.errors.map((e, i) => <li key={`${e.row}-${i}`}>Row {e.row}: {e.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Sage (Pastel) supplier / sub-contractor importer
// ============================================================
function SageVendorImportCard({ onChange }) {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lastUndo, setLastUndo] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const loadLastUndo = () => api.get("/import/sage-vendors/last-undo")
    .then((r) => setLastUndo(r.data?.available ? r.data : null)).catch(() => {});
  useEffect(() => { loadLastUndo(); }, []);

  const start = async () => {
    if (!file) return toast.error("Choose the Sage vendors .xlsx first");
    if (!dryRun && !window.confirm(
      "Commit the supplier (sub-contractor) import?\n\n" +
      "Matched suppliers are updated (blank cells never overwrite existing data) " +
      "and unmatched rows are created. Extra ';'-separated contacts/emails/numbers " +
      "are stored on the record. Run a dry-run first if unsure. Audited and undoable."
    )) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");

      const resp = await fetch(`${API}/import/sage-vendors`, {
        method: "POST", body: fd, headers: authHeaders(),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || resp.statusText);
      }
      const data = await resp.json();
      setResult(data);
      toast.success(dryRun ? "Dry-run complete — review the preview below" : "Suppliers imported");
      loadLastUndo();
      onChange && onChange();
    } catch (e) {
      toast.error(e.message || apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!lastUndo) return;
    if (!window.confirm(
      `Undo the last supplier import (${lastUndo.filename})?\n\n` +
      `This will DELETE ${lastUndo.created_vendors} supplier(s) it created and restore ` +
      `${lastUndo.updated_vendors} updated supplier(s) to their previous values. Cannot be redone.`
    )) return;
    setUndoing(true);
    try {
      const fd = new FormData();
      fd.append("undo_id", lastUndo.id);

      const resp = await fetch(`${API}/import/sage-vendors/undo`, {
        method: "POST", body: fd, headers: authHeaders(),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || resp.statusText);
      }
      const d = await resp.json();
      toast.success(`Undone — ${d.vendors_deleted} deleted, ${d.vendors_restored} restored`);
      setLastUndo(null);
      onChange && onChange();
    } catch (e) {
      toast.error(e.message || apiErr(e));
    } finally {
      setUndoing(false);
    }
  };

  const r = result || {};
  const updated = (r.updated_by_id || 0) + (r.updated_by_number || 0) + (r.updated_by_name || 0);

  return (
    <div className="card pad" style={{ borderLeft: "3px solid var(--blue)", gridColumn: "1 / -1" }} data-testid="sage-vendor-card">
      <div className="sec-h"><h3>Sage supplier (sub-contractor) load &amp; correct (one-off)</h3><GitMerge size={18} color="var(--blue)" /></div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>
        Upload the merged Sage vendors workbook (<code className="mono">PTE_Connect_Vendors_Sage…xlsx</code>). Reads the <b>PTE Connect Upload</b> sheet.
        Existing suppliers are matched by <b>id → vendor number → name</b> and <b>updated</b> (blank cells never wipe existing data); rows with no match are <b>created new</b>.
        Multiple <span className="mono">;</span>-separated <b>email / contact / number</b> values keep the first as primary; the rest are saved on the record.
      </p>

      {lastUndo && (
        <div className="card pad" style={{ marginBottom: 12, background: "rgba(60,130,210,.08)", borderLeft: "3px solid var(--blue)" }} data-testid="sage-vendor-undo-banner">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <AlertTriangle size={15} color="var(--blue)" />
            <span style={{ fontSize: 12.5 }}>
              Last commit (<b>{lastUndo.filename}</b>) can be undone — created <b>{lastUndo.created_vendors}</b>, updated <b>{lastUndo.updated_vendors}</b>.
            </span>
            <button className="btn sm" onClick={undo} disabled={undoing} data-testid="sage-vendor-undo-btn" style={{ marginLeft: "auto" }}>
              <RefreshCw size={13} className={undoing ? "spin" : ""} /> {undoing ? "Undoing…" : "Undo last import"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label className="btn ghost" style={{ cursor: "pointer" }}>
          <Upload size={14} /> {file ? "Replace file" : "Choose .xlsx"}
          <input type="file" accept=".xlsx,.xlsm" hidden onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="sage-vendor-file" disabled={busy} />
        </label>
        {file && <span className="t-mut" style={{ fontSize: 12 }}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={busy} data-testid="sage-vendor-dryrun" />
          Dry-run (preview only, no writes)
        </label>
        <button className="btn gold" onClick={start} disabled={busy || !file} data-testid="sage-vendor-import-start" style={{ marginLeft: "auto" }}>
          <Play size={14} /> {busy ? "Working…" : (dryRun ? "Run dry-run" : "Import now")}
        </button>
      </div>

      {result && (
        <div className="card pad" style={{ marginTop: 8, background: "var(--card-2)" }} data-testid="sage-vendor-result">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className={`badge ${r.dry_run ? "b-slate" : "b-green"}`}><span className="bd" />{r.dry_run ? "Dry-run preview" : "Committed"}</span>
            <span className="s t-mut mono">{(r.rows_read || 0).toLocaleString()} rows read</span>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 6, fontSize: 12.5 }}>
            <KV label="Suppliers created" value={r.vendors_created} highlight />
            <KV label="Suppliers updated" value={updated} highlight />
            <KV label="Fields updated" value={r.fields_updated} />
            <KV label="Matched by id" value={r.updated_by_id} />
            <KV label="Matched by number" value={r.updated_by_number} />
            <KV label="Matched by name" value={r.updated_by_name} />
            <KV label="Rows with extra contacts" value={r.extra_contacts_stored} highlight />
            <KV label="Skipped (blank name)" value={r.skipped_blank_name} />
            <KV label="Errors" value={r.total_errors} danger={r.total_errors > 0} />
          </div>
          {r.errors && r.errors.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--amber)" }}>{r.errors.length} sample error(s)</summary>
              <ul style={{ fontSize: 11.5, marginTop: 6 }}>
                {r.errors.map((e, i) => <li key={`${e.row}-${i}`}>Row {e.row}: {e.error}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
//  Caspio ↔ PTE Connect — Reconciliation / Diff
// ============================================================
const BUCKETS = [
  { key: "missing_in_pte_connect", label: "Missing in PTE Connect", colour: "var(--red)",
    note: "Rows in the Caspio file that don't exist in PTE Connect — these would be created on a real import." },
  { key: "missing_in_caspio", label: "Missing in Caspio", colour: "var(--amber)",
    note: "Rows in PTE Connect that aren't in the uploaded Caspio file — created post-take-on or in a stale export." },
  { key: "drift", label: "Calibration field drift", colour: "var(--blue)",
    note: "Same equipment on both sides, but at least one of last_cal / cal_due / last_cert / period disagrees." },
];

function CaspioDiffCard() {
  const [file, setFile] = useState(null);
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [reimporting, setReimporting] = useState(false);
  const [reimportBatch, setReimportBatch] = useState(null);
  const pollRef = useRef(null);
  const reimportPollRef = useRef(null);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (reimportPollRef.current) clearInterval(reimportPollRef.current);
  }, []);

  const start = async () => {
    if (!file) return toast.error("Pick a Caspio Equipment_Database CSV first");
    setBusy(true);
    setDiff(null);
    setOpen(null);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const resp = await fetch(`${API}/import/diff`, {
        method: "POST", body: fd, headers: authHeaders(),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || resp.statusText);
      }
      const { diff_id } = await resp.json();
      toast.success("Diff started — comparing against the live register…");
      const tick = async () => {
        try {
          const r = await api.get(`/import/diff/${diff_id}`);
          setDiff(r.data);
          if (r.data.status === "completed" || r.data.status === "failed") {
            clearInterval(pollRef.current); pollRef.current = null;
            setBusy(false);
          }
        } catch (_) {}
      };
      pollRef.current = setInterval(tick, 2000);
      tick();
    } catch (e) {
      toast.error(e.message || apiErr(e));
      setBusy(false);
    }
  };

  const downloadCsv = async (bucket) => {
    try {

      const resp = await fetch(`${API}/import/diff/${diff.id}/csv?bucket=${bucket}`,
        { headers: authHeaders() });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `caspio-diff-${bucket}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  const reimportMissing = async () => {
    const missing = diff?.counts?.missing_in_pte_connect || 0;
    if (!missing) return;
    if (!window.confirm(
      `Re-import ${missing.toLocaleString()} missing row${missing === 1 ? "" : "s"} into PTE Connect?\n\n` +
      `This re-runs the Caspio importer in commit mode against the same file. ` +
      `Because the importer is idempotent, only rows that don't exist yet are created — ` +
      `everything else is skipped automatically. This cannot be undone, but is safe to repeat.`
    )) return;
    setReimporting(true);
    setReimportBatch(null);
    try {
      const r = await api.post(`/import/diff/${diff.id}/reimport-missing`);
      const { batch_id } = r.data;
      toast.success(`Re-import started — targeting ~${missing.toLocaleString()} missing row(s)`);
      const tick = async () => {
        try {
          const br = await api.get(`/import/batches/${batch_id}`);
          setReimportBatch(br.data);
          if (br.data.status === "completed" || br.data.status === "failed") {
            clearInterval(reimportPollRef.current);
            reimportPollRef.current = null;
            setReimporting(false);
          }
        } catch (_) {}
      };
      reimportPollRef.current = setInterval(tick, 2000);
      tick();
    } catch (e) {
      toast.error(apiErr(e));
      setReimporting(false);
    }
  };

  const c = diff?.counts || {};

  return (
    <div className="card pad" style={{ borderLeft: "3px solid var(--blue)", gridColumn: "1 / -1" }}>
      <div className="sec-h">
        <h3>Reconcile against Caspio export</h3>
        <GitCompareArrows size={18} color="var(--blue)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>
        Upload the same wide <code className="mono">Equipment_Database</code> CSV and we'll compare it row-by-row against PTE Connect (same <b>customer + serial/asset/description</b> dedup key used by the importer).
        Three buckets: <b style={{ color: "var(--red)" }}>missing in PTE Connect</b>, <b style={{ color: "var(--amber)" }}>missing in Caspio</b>, and <b style={{ color: "var(--blue)" }}>calibration field drift</b>. Read-only — nothing is written to the DB.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label className="btn ghost" style={{ cursor: "pointer" }}>
          <Upload size={14} /> {file ? "Replace file" : "Choose CSV"}
          <input type="file" accept=".csv" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="diff-file" disabled={busy} />
        </label>
        {file && <span className="t-mut" style={{ fontSize: 12 }}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
        <button className="btn gold" onClick={start} disabled={busy || !file} data-testid="diff-start" style={{ marginLeft: "auto" }}>
          <Play size={14} /> {busy ? "Comparing…" : "Run diff"}
        </button>
      </div>

      {diff && (
        <div className="card pad" style={{ marginTop: 8, background: "var(--card-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span className={`badge ${diff.status === "completed" ? "b-green" : diff.status === "failed" ? "b-red" : "b-blue"}`}>
              <span className="bd" />{diff.status}
            </span>
            <span className="s t-mut mono">{diff.filename}</span>
          </div>
          {diff.error && <div style={{ color: "var(--red)", fontSize: 12.5, marginBottom: 8 }}>{diff.error}</div>}

          <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 6, fontSize: 12.5, marginBottom: 10 }}>
            <KV label="File rows seen" value={c.file_rows_seen} />
            <KV label="Unique file keys" value={c.file_keys_unique} />
            <KV label="DB rows scanned" value={c.db_rows_seen} />
            <KV label="Missing in PTE Connect" value={c.missing_in_pte_connect} danger={c.missing_in_pte_connect > 0} />
            <KV label="Missing in Caspio" value={c.missing_in_caspio} highlight />
            <KV label="Drift (cal fields differ)" value={c.drift} highlight />
            <KV label="Exact matches" value={c.match_exact} />
          </div>

          {diff.status === "completed" && BUCKETS.map((b) => {
            const rows = diff.samples?.[b.key] || [];
            const total = c[b.key] || 0;
            const isMissingPte = b.key === "missing_in_pte_connect";
            return (
              <div key={b.key} style={{ marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <button className="btn sm ghost" onClick={() => setOpen(open === b.key ? null : b.key)}
                          data-testid={`diff-bucket-${b.key}`}>
                    {open === b.key ? "▾" : "▸"} <b style={{ color: b.colour }}>{b.label}</b>
                    <span className="mono t-mut" style={{ marginLeft: 6 }}>· {total.toLocaleString()}</span>
                  </button>
                  {total > 0 && (
                    <button className="btn sm" onClick={() => downloadCsv(b.key)}
                            data-testid={`diff-download-${b.key}`}>
                      <Download size={12} /> Download all {total.toLocaleString()} as CSV
                    </button>
                  )}
                  {isMissingPte && total > 0 && (
                    <button className="btn sm gold" onClick={reimportMissing}
                            disabled={reimporting}
                            data-testid="diff-reimport-missing">
                      <Zap size={12} /> {reimporting ? "Re-importing…" : `Re-import ${total.toLocaleString()} missing`}
                    </button>
                  )}
                  <span className="t-mut" style={{ fontSize: 11.5, flex: "1 1 100%" }}>{b.note}</span>
                </div>
                {isMissingPte && reimportBatch && (
                  <div className="card pad" style={{ marginTop: 8, background: "var(--card-2)", borderLeft: "3px solid var(--gold-deep)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span className={`badge ${reimportBatch.status === "completed" ? "b-green" : reimportBatch.status === "failed" ? "b-red" : "b-blue"}`}>
                        <span className="bd" />{reimportBatch.status}
                      </span>
                      <span className="s t-mut">Re-import progress</span>
                    </div>
                    {reimportBatch.error && <div style={{ color: "var(--red)", fontSize: 12.5, marginBottom: 6 }}>{reimportBatch.error}</div>}
                    <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 6, fontSize: 12.5 }}>
                      <KV label="Rows seen" value={reimportBatch.counts?.rows_seen} />
                      <KV label="Equipment created" value={reimportBatch.counts?.equipment_created} highlight />
                      <KV label="Equipment enriched" value={reimportBatch.counts?.equipment_enriched} />
                      <KV label="Certificates created" value={reimportBatch.counts?.certificates_created} highlight />
                      <KV label="Customers created" value={reimportBatch.counts?.customers_created} />
                      <KV label="Errors" value={reimportBatch.counts?.errors} danger={reimportBatch.counts?.errors > 0} />
                    </div>
                  </div>
                )}
                {open === b.key && (
                  <div className="tbl-wrap" style={{ marginTop: 8 }}>
                    {rows.length === 0 ? (
                      <div className="t-mut" style={{ fontSize: 12.5, padding: "8px 4px" }}>Nothing in this bucket — clean reconciliation.</div>
                    ) : (
                      <DiffTable bucket={b.key} rows={rows} total={total} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DiffTable({ bucket, rows, total }) {
  if (bucket === "drift") {
    return (
      <table data-testid={`diff-table-${bucket}`}>
        <thead><tr><th>Customer</th><th>Serial</th><th>Asset</th><th>Description</th><th>Field</th><th>Caspio</th><th>PTE</th></tr></thead>
        <tbody>
          {rows.flatMap((r) => (r.drift_fields || []).map((f) => (
            <tr key={`${r.dedup_key}-${f}`}>
              <td className="t-strong">{r.customer_name || "—"}</td>
              <td className="mono t-mut">{r.serial_number || "—"}</td>
              <td className="mono t-mut">{r.asset_number || "—"}</td>
              <td>{r.description || "—"}</td>
              <td className="mono" style={{ fontSize: 11.5 }}>{f}</td>
              <td className="mono" style={{ color: "var(--red)" }}>{r[`file_${f}`] || "(empty)"}</td>
              <td className="mono" style={{ color: "var(--blue)" }}>{r[`db_${f}`] || "(empty)"}</td>
            </tr>
          )))}
        </tbody>
      </table>
    );
  }
  const isPte = bucket === "missing_in_pte_connect";
  const pfx = isPte ? "file_" : "db_";
  return (
    <table data-testid={`diff-table-${bucket}`}>
      <thead><tr><th>Customer</th><th>Serial</th><th>Asset</th><th>Description</th><th>Discipline</th><th>Last cal</th><th>Cal due</th><th>Cert #</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.dedup_key}>
            <td className="t-strong">{r.customer_name || "—"}</td>
            <td className="mono t-mut">{r.serial_number || "—"}</td>
            <td className="mono t-mut">{r.asset_number || "—"}</td>
            <td>{r.description || "—"}</td>
            <td className="t-mut">{r.discipline || "—"}</td>
            <td className="mono">{r[`${pfx}last_calibration_date`] || "—"}</td>
            <td className="mono">{r[`${pfx}calibration_due_date`] || "—"}</td>
            <td className="mono">{r[`${pfx}last_certificate_number`] || "—"}</td>
          </tr>
        ))}
        {rows.length < total && (
          <tr><td colSpan={8} className="t-mut" style={{ textAlign: "center", fontSize: 12 }}>
            Showing first {rows.length} of {total.toLocaleString()} — download the CSV for the full list.
          </td></tr>
        )}
      </tbody>
    </table>
  );
}


// ============================================================
//  Take-on Cleanup Wizard — Dedupe → Diff → Re-import missing
// ============================================================
const WIZARD_STEPS = [
  { key: "scan_eq", label: "Scan equipment duplicates" },
  { key: "merge_eq", label: "Auto-merge duplicate clusters" },
  { key: "diff", label: "Run diff against the uploaded file" },
  { key: "reimport", label: "Re-import rows still missing" },
];

function TakeOnCleanupWizardCard() {
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [stepStatus, setStepStatus] = useState({});
  const [log, setLog] = useState([]);
  const [summary, setSummary] = useState(null);

  const addLog = (msg) => setLog((l) => [...l, { at: new Date().toLocaleTimeString(), msg }]);
  const setStep = (k, v) => setStepStatus((s) => ({ ...s, [k]: v }));

  const waitForDiff = async (diffId) => {
    for (let i = 0; i < 240; i++) { // up to ~8 min
      const r = await api.get(`/import/diff/${diffId}`);
      if (r.data.status === "completed") return r.data;
      if (r.data.status === "failed") throw new Error(r.data.error || "Diff failed");
      await new Promise((res) => setTimeout(res, 2000));
    }
    throw new Error("Diff timed out");
  };

  const waitForBatch = async (batchId) => {
    for (let i = 0; i < 240; i++) {
      const r = await api.get(`/import/batches/${batchId}`);
      if (r.data.status === "completed") return r.data;
      if (r.data.status === "failed") throw new Error(r.data.error || "Re-import failed");
      await new Promise((res) => setTimeout(res, 2000));
    }
    throw new Error("Re-import timed out");
  };

  const run = async () => {
    if (!file) return toast.error("Pick a Caspio Equipment_Database CSV first");
    if (!window.confirm(
      `Run the full take-on cleanup against ${file.name}?\n\n` +
      `Steps:\n` +
      ` 1. Scan & auto-merge any duplicate equipment in the live register (preserves all calibration history)\n` +
      ` 2. Upload the file and run a diff against the now-clean register\n` +
      ` 3. Re-import every row from the file that's still missing\n\n` +
      `This typically takes 1–5 minutes for a full Caspio dump. Everything is audited.`
    )) return;

    setRunning(true);
    setDone(false);
    setLog([]);
    setStepStatus({});
    setSummary(null);
    const out = {};
    try {
      // 1) Dedupe — scan equipment duplicates
      setStep("scan_eq", "running");
      addLog("Scanning live register for duplicate equipment…");
      const ed = await api.get("/dedupe/equipment", { params: { limit: 500 } });
      setStep("scan_eq", "done");
      addLog(`Found ${ed.data.total_clusters} duplicate cluster(s) (${ed.data.total_duplicate_rows} dup row(s)).`);

      // 2) Auto-merge
      if (ed.data.clusters.length === 0) {
        setStep("merge_eq", "skipped");
        addLog("No duplicates to merge — register already clean.");
        out.clusters_merged = 0; out.duplicates_deleted = 0;
      } else {
        setStep("merge_eq", "running");
        addLog(`Auto-merging ${ed.data.clusters.length} cluster(s)…`);
        const payload = {
          clusters: ed.data.clusters.map((c) => ({
            master_id: c.master.id, duplicate_ids: c.duplicates.map((d) => d.id),
          })),
        };
        const m = await api.post("/dedupe/equipment/merge-bulk", payload);
        setStep("merge_eq", "done");
        addLog(
          `Merged ${m.data.clusters_merged} cluster(s) · ${m.data.duplicates_deleted} dup(s) deleted · ` +
          `${m.data.certs_moved} cert(s) + ${m.data.events_moved} event(s) + ${m.data.job_items_moved} job-line(s) moved.` +
          (m.data.clusters_failed ? ` ${m.data.clusters_failed} failed.` : "")
        );
        out.clusters_merged = m.data.clusters_merged;
        out.duplicates_deleted = m.data.duplicates_deleted;
      }

      // 3) Diff
      setStep("diff", "running");
      addLog(`Uploading ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB) and running diff…`);
      const fd = new FormData();
      fd.append("file", file);

      const resp = await fetch(`${API}/import/diff`, {
        method: "POST", body: fd, headers: authHeaders(),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || resp.statusText);
      }
      const { diff_id } = await resp.json();
      addLog(`Diff started (id ${diff_id}). Comparing dedup keys…`);
      const diffRes = await waitForDiff(diff_id);
      setStep("diff", "done");
      const c = diffRes.counts || {};
      addLog(
        `Diff complete · ${c.file_rows_seen} file rows · ` +
        `${c.missing_in_pte_connect} missing in PTE · ${c.drift} drift · ` +
        `${c.match_exact} exact matches · ${c.missing_in_caspio} only-in-PTE`
      );
      out.diff_id = diff_id;
      out.diff_counts = c;

      // 4) Re-import missing
      const missing = c.missing_in_pte_connect || 0;
      if (missing === 0) {
        setStep("reimport", "skipped");
        addLog("Nothing missing — re-import skipped.");
        out.equipment_created = 0;
      } else {
        setStep("reimport", "running");
        addLog(`Re-importing ${missing.toLocaleString()} missing row(s)…`);
        const ri = await api.post(`/import/diff/${diff_id}/reimport-missing`);
        const batchId = ri.data.batch_id;
        addLog(`Re-import batch ${batchId} started. Streaming progress…`);
        const batch = await waitForBatch(batchId);
        setStep("reimport", "done");
        const bc = batch.counts || {};
        addLog(
          `Re-import complete · ${bc.equipment_created} equipment + ${bc.certificates_created || 0} cert(s) created · ` +
          `${bc.equipment_enriched || 0} enriched · ${bc.errors} error(s).`
        );
        out.equipment_created = bc.equipment_created;
        out.errors = bc.errors;
      }

      setSummary(out);
      setDone(true);
      toast.success("Take-on cleanup complete — register synced to the Caspio file.");
    } catch (e) {
      addLog(`ERROR: ${e.message || apiErr(e)}`);
      toast.error(e.message || apiErr(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="card pad" style={{ borderLeft: "3px solid var(--gold-deep)", gridColumn: "1 / -1" }}>
      <div className="sec-h">
        <h3>Take-on cleanup wizard — one-click full reconciliation</h3>
        <Sparkles size={18} color="var(--gold-deep)" />
      </div>
      <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>
        Closes the take-on loop in one click. Drop the latest Caspio <code className="mono">Equipment_Database</code> CSV, and the wizard will (1) <b>dedupe</b> the live register, (2) <b>diff</b> the file against the now-clean register, (3) <b>re-import</b> any rows still missing. All audited; nothing is mutated until the diff is done and you've already approved the merge with the confirm.
      </p>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <label className="btn ghost" style={{ cursor: "pointer" }}>
          <Upload size={14} /> {file ? "Replace file" : "Choose CSV"}
          <input type="file" accept=".csv" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="wiz-file" disabled={running} />
        </label>
        {file && <span className="t-mut" style={{ fontSize: 12 }}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</span>}
        <button className="btn gold" onClick={run} disabled={running || !file} data-testid="wiz-run" style={{ marginLeft: "auto" }}>
          {running ? <><RefreshCw size={14} className="spin" /> Running…</> : <><Sparkles size={14} /> Run full cleanup</>}
        </button>
      </div>

      <div className="card pad" style={{ background: "var(--card-2)" }}>
        {WIZARD_STEPS.map((st) => {
          const s = stepStatus[st.key];
          const Icon = s === "done" ? CheckCircle2 : s === "running" ? RefreshCw : ChevronRight;
          const color = s === "done" ? "var(--green)" : s === "running" ? "var(--gold-deep)" : s === "skipped" ? "var(--text-2)" : "var(--text-2)";
          return (
            <div key={st.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 13 }}>
              <Icon size={15} className={s === "running" ? "spin" : ""} style={{ color }} />
              <span style={{ color: s ? "var(--text-1)" : "var(--text-2)" }}>{st.label}</span>
              {s && s !== "running" && <span className="t-mut" style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".05em" }}>{s}</span>}
            </div>
          );
        })}
      </div>

      {done && summary && (
        <div className="card pad" style={{ marginTop: 10, background: "rgba(20,140,80,.08)", borderLeft: "3px solid var(--green)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <CheckCircle2 size={15} color="var(--green)" />
            <b>Cleanup complete</b>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 6, fontSize: 12.5 }}>
            <KV label="Clusters merged" value={summary.clusters_merged} highlight />
            <KV label="Dup rows deleted" value={summary.duplicates_deleted} />
            <KV label="Equipment re-imported" value={summary.equipment_created} highlight />
            <KV label="Diff drift count" value={summary.diff_counts?.drift} />
            <KV label="Exact matches" value={summary.diff_counts?.match_exact} />
            <KV label="Re-import errors" value={summary.errors} danger={summary.errors > 0} />
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div className="card pad" style={{ marginTop: 10, background: "#0d0d10", color: "#d9d7d0", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, maxHeight: 320, overflowY: "auto" }}>
          {log.map((l, i) => (
            <div key={`${l.at}-${i}`} style={{ padding: "2px 0" }}>
              <span style={{ color: "#7a7770", marginRight: 8 }}>{l.at}</span>
              <span>{l.msg}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
